// useEntityDetails — React Query hooks för entity_details + attachments.
// Anropar RPCs definierade i migration 20260430100000.
//
// Storage path-konvention (matchar storage-policy):
//   <workflow_id>/<entity_id>/<uuid>-<filename>
// Bucket: entity-attachments (private, 10 MB cap)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export type EntityType = 'block' | 'group' | 'layer' | 'connector';

export interface EntityDetailsRow {
  id: string;
  workflow_id: string;
  entity_id: string;
  entity_type: EntityType;
  description_md: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface EntityAttachmentRow {
  id: string;
  details_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  uploaded_by: string | null;
  uploaded_at: string;
}

export interface EntityDetailsResult {
  success: boolean;
  error?: string;
  details: EntityDetailsRow | null;
  attachments: EntityAttachmentRow[];
}

const ATTACHMENTS_BUCKET = 'entity-attachments';
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

export function useEntityDetails(workflowId: string | null, entityId: string | null) {
  return useQuery({
    queryKey: ['entity-details', workflowId, entityId],
    queryFn: async (): Promise<EntityDetailsResult> => {
      if (!workflowId || !entityId) {
        return { success: false, error: 'missing-args', details: null, attachments: [] };
      }
      const { data, error } = await supabase.rpc('get_entity_details', {
        p_workflow_id: workflowId,
        p_entity_id: entityId,
      });
      if (error) {
        return { success: false, error: error.message, details: null, attachments: [] };
      }
      const result = data as unknown as EntityDetailsResult | null;
      return result ?? { success: false, error: 'no-data', details: null, attachments: [] };
    },
    enabled: !!workflowId && !!entityId,
    staleTime: 30_000,
  });
}

export function useUpsertEntityDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      workflowId: string;
      entityId: string;
      entityType: EntityType;
      descriptionMd: string;
      metadata?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc('upsert_entity_details', {
        p_workflow_id: args.workflowId,
        p_entity_id: args.entityId,
        p_entity_type: args.entityType,
        p_description_md: args.descriptionMd,
        p_metadata: JSON.parse(JSON.stringify(args.metadata ?? {})),
      });
      if (error) throw error;
      const result = data as unknown as { success: boolean; error?: string; id?: string };
      if (!result.success) throw new Error(result.error ?? 'upsert-failed');
      return result;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['entity-details', vars.workflowId, vars.entityId] });
    },
    onError: (err) => {
      toast({
        title: 'Kunde inte spara details',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    },
  });
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      workflowId: string;
      entityId: string;
      entityType: EntityType;
      file: File;
    }) => {
      if (args.file.size > MAX_FILE_BYTES) {
        throw new Error(`Filen är för stor (max ${MAX_FILE_BYTES / 1024 / 1024} MB).`);
      }
      const cleanName = sanitizeFilename(args.file.name);
      const objectPath = `${args.workflowId}/${args.entityId}/${crypto.randomUUID()}-${cleanName}`;

      const { error: uploadErr } = await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .upload(objectPath, args.file, {
          contentType: args.file.type || 'application/octet-stream',
          upsert: false,
        });
      if (uploadErr) throw uploadErr;

      const { data, error } = await supabase.rpc('register_entity_attachment', {
        p_workflow_id: args.workflowId,
        p_entity_id: args.entityId,
        p_entity_type: args.entityType,
        p_filename: args.file.name,
        p_mime_type: args.file.type || 'application/octet-stream',
        p_size_bytes: args.file.size,
        p_storage_path: objectPath,
      });
      if (error) {
        // Rollback storage-uploaden om RPC misslyckas så vi inte får orphan-files.
        await supabase.storage.from(ATTACHMENTS_BUCKET).remove([objectPath]);
        throw error;
      }
      const result = data as unknown as { success: boolean; error?: string; attachment_id?: string };
      if (!result.success) {
        await supabase.storage.from(ATTACHMENTS_BUCKET).remove([objectPath]);
        throw new Error(result.error ?? 'register-failed');
      }
      return result;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['entity-details', vars.workflowId, vars.entityId] });
    },
    onError: (err) => {
      toast({
        title: 'Upload misslyckades',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      attachmentId: string;
      workflowId: string;
      entityId: string;
    }) => {
      const { data, error } = await supabase.rpc('delete_entity_attachment', {
        p_attachment_id: args.attachmentId,
      });
      if (error) throw error;
      const result = data as unknown as { success: boolean; error?: string; storage_path?: string };
      if (!result.success) throw new Error(result.error ?? 'delete-failed');
      // Vi raderar storage-objektet efter DB-radering så att orphan-row
      // aldrig kan peka på en redan borttagen fil.
      if (result.storage_path) {
        await supabase.storage.from(ATTACHMENTS_BUCKET).remove([result.storage_path]);
      }
      return result;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['entity-details', vars.workflowId, vars.entityId] });
    },
    onError: (err) => {
      toast({
        title: 'Kunde inte radera bilaga',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    },
  });
}

/**
 * Returnerar en signed-url (60s TTL) för en attachment så frontend kan
 * preview:a / download:a den. Storage-bucket är private — vi kan inte
 * länka direkt utan signed-url.
 */
export async function getAttachmentSignedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 60);
  if (error || !data) return null;
  return data.signedUrl;
}

/**
 * Download a text/markdown attachment as a string. Used by
 * EntityDetailsDialog to hydrate the markdown editor when a long
 * `description_md` body was previously routed to the storage bucket
 * (metadata flag `_detail_in_bucket: true`). Returns null on any
 * network / signed-URL failure so the caller can fall back to the
 * empty editor without throwing.
 */
export async function fetchAttachmentText(
  storagePath: string,
): Promise<string | null> {
  const url = await getAttachmentSignedUrl(storagePath);
  if (!url) return null;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    return await resp.text();
  } catch {
    return null;
  }
}

export interface EntityDetailsIndexEntry {
  hasDescription: boolean;
  attachmentCount: number;
}

/**
 * Hämtar alla entity_details + räknar attachments per entity för ett
 * workflow_id. Används för att visa indicators (paperclip / note-ikon)
 * på blocks/groups utan att behöva separat query per entity.
 */
export function useEntityDetailsIndex(workflowId: string | null) {
  return useQuery({
    queryKey: ['entity-details-index', workflowId],
    queryFn: async (): Promise<Record<string, EntityDetailsIndexEntry>> => {
      if (!workflowId) return {};
      const { data, error } = await supabase
        .from('entity_details')
        .select('entity_id, description_md, entity_attachments(id)')
        .eq('workflow_id', workflowId);
      if (error || !data) return {};
      const out: Record<string, EntityDetailsIndexEntry> = {};
      type Row = {
        entity_id: string;
        description_md: string | null;
        entity_attachments?: Array<{ id: string }>;
      };
      for (const row of data as unknown as Row[]) {
        out[row.entity_id] = {
          hasDescription: !!(row.description_md && row.description_md.trim().length > 0),
          attachmentCount: row.entity_attachments?.length ?? 0,
        };
      }
      return out;
    },
    enabled: !!workflowId,
    staleTime: 30_000,
  });
}
